import React, { useState, useCallback } from 'react';
import {
  Box,
  Image,
  Text,
  Badge,
  VStack,
  HStack,
  IconButton,
  Button,
  Divider,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiEye, FiEdit2, FiMapPin } from 'react-icons/fi';
import { APIProvider, Map, AdvancedMarker, InfoWindow, useAdvancedMarkerRef } from "@vis.gl/react-google-maps";
import { Device } from '@/services/letsparkyClient';

// Default map configuration
const INITIAL_MAP_REGION = {
  center: { lat: 40.7128, lng: -74.0060 }, // New York City as default
  zoom: 12
};

interface DeviceMapProps {
  devices: Device[];
  isLoading?: boolean;
  onDeviceView?: (device: Device) => void;
  onDeviceEdit?: (device: Device) => void;
  height?: string;
}

interface MarkerWithInfoWindowProps {
  device: Device;
  infoWindowId?: string;
  setInfoWindowId: (id: string) => void;
  onDeviceView?: (device: Device) => void;
  onDeviceEdit?: (device: Device) => void;
}

const MarkerWithInfoWindow: React.FC<MarkerWithInfoWindowProps> = ({
  device,
  infoWindowId,
  setInfoWindowId,
  onDeviceView,
  onDeviceEdit,
}) => {
  const [markerRef, marker] = useAdvancedMarkerRef();

  const handleMarkerClick = useCallback(() => {
    setInfoWindowId(device.id);
  }, [device.id, setInfoWindowId]);

  const handleClose = useCallback(() => {
    setInfoWindowId("");
  }, [setInfoWindowId]);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'online':
        return 'green';
      case 'inactive':
      case 'offline':
        return 'red';
      case 'maintenance':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const calculateBatteryPercentage = (voltage: number) => {
    const percentage = Math.round(((voltage - 5000)/(6500-5000)) * 100);
    return percentage < 100 ? percentage : 100;
  };

  const getBatteryColor = (voltage: number) => {
    const percentage = calculateBatteryPercentage(voltage);
    if (percentage < 20) return "red";
    if (percentage < 40) return "#ff5220";
    if (percentage < 50) return "orange";
    if (percentage < 70) return "#52aa20";
    if (percentage < 90) return "#20aa20";
    return "#20ee20";
  };

  const getMarkerImage = () => {
    // Commented out terminal type logic for now - can be re-enabled when needed
    // if (device?.terminalType === "ARM_BARRIER") {
    //   return "/markers/terminal_gate_barrier.png";
    // } else if (device?.terminalType === "ELECTRIC_GATE") {
    //   return "/markers/terminal_gate_electric.png";
    // } else if (device?.terminalType === "GARAGE_GATE") {
    //   return "/markers/terminal_gate_garage.png";
    // } else if (device?.terminalType === "RISING_BOLLARD") {
    //   return "/markers/terminal_gate_bollard.png";
    // } else {
    //   return "/markers/bouncer.png";
    // }
    return "/markers/bouncer.png";
  };

  // Check if device has valid coordinates
  const hasLocation = device.geo_lat && device.geo_lng && 
    !isNaN(Number(device.geo_lat)) && !isNaN(Number(device.geo_lng));

  if (!hasLocation) return null;

  return (
    <>
      <AdvancedMarker
        ref={markerRef}
        position={{
          lat: Number(device.geo_lat),
          lng: Number(device.geo_lng),
        }}
        onClick={handleMarkerClick}
      >
        <Image alt="marker" src={getMarkerImage()} width={50} />
      </AdvancedMarker>

      {infoWindowId === device.id && (
        <InfoWindow anchor={marker} onClose={handleClose}>
          <Box w="280px" p={2}>
            <VStack spacing={3} align="stretch">
              {/* Header */}
              <VStack spacing={1} align="stretch">
                <Text fontSize="lg" fontWeight="bold" color="gray.800">
                  {device.nick_name || device.serial_no}
                </Text>
                <HStack>
                  <Badge colorScheme={getStatusColor(device.status)} size="sm">
                    {device.status}
                  </Badge>
                  {device.sensor && (
                    <Badge colorScheme="blue" size="sm">
                      Sensor
                    </Badge>
                  )}
                </HStack>
              </VStack>

              {/* Device Image */}
              {device.photo && (
                <Box>
                  <Image
                    src={device.photo}
                    alt={device.nick_name || "Device"}
                    borderRadius="md"
                    maxH="120px"
                    w="100%"
                    objectFit="cover"
                    fallback={
                      <Box
                        h="120px"
                        bg="gray.100"
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="gray.500">No Image</Text>
                      </Box>
                    }
                  />
                </Box>
              )}

              {/* Device Details */}
              <VStack spacing={2} align="stretch" fontSize="sm">
                <HStack justify="space-between">
                  <Text color="gray.600">Serial:</Text>
                  <Text fontWeight="medium">{device.serial_no}</Text>
                </HStack>
                
                {device.imei_number && (
                  <HStack justify="space-between">
                    <Text color="gray.600">IMEI:</Text>
                    <Text fontWeight="medium">{device.imei_number}</Text>
                  </HStack>
                )}

                {device.qr_data && (
                  <HStack justify="space-between">
                    <Text color="gray.600">Short ID:</Text>
                    <Text fontWeight="medium">{device.qr_data}</Text>
                  </HStack>
                )}

                {device.location && (
                  <HStack justify="space-between">
                    <Text color="gray.600">Location:</Text>
                    <Text fontWeight="medium">{device.location}</Text>
                  </HStack>
                )}

                {device.battery_voltage && (
                  <HStack justify="space-between">
                    <Text color="gray.600">Battery:</Text>
                    <Badge color={getBatteryColor(device.battery_voltage)} variant="subtle">
                      {device.battery_voltage}mV ({calculateBatteryPercentage(device.battery_voltage)}%)
                    </Badge>
                  </HStack>
                )}

                {device.last_connection_date && (
                  <VStack align="stretch" spacing={1}>
                    <Text color="gray.600">Last Seen:</Text>
                    <Text fontSize="xs" color="gray.500">
                      {new Date(device.last_connection_date).toLocaleString()}
                    </Text>
                  </VStack>
                )}
              </VStack>

              {/* Action Buttons */}
              <Divider />
              <HStack justify="space-around">
                {onDeviceView && (
                  <Button
                    size="sm"
                    leftIcon={<FiEye />}
                    colorScheme="blue"
                    variant="ghost"
                    onClick={() => onDeviceView(device)}
                  >
                    View
                  </Button>
                )}
                {onDeviceEdit && (
                  <Button
                    size="sm"
                    leftIcon={<FiEdit2 />}
                    colorScheme="teal"
                    variant="ghost"
                    onClick={() => onDeviceEdit(device)}
                  >
                    Edit
                  </Button>
                )}
              </HStack>
            </VStack>
          </Box>
        </InfoWindow>
      )}
    </>
  );
};

const DeviceMap: React.FC<DeviceMapProps> = ({
  devices,
  isLoading = false,
  onDeviceView,
  onDeviceEdit,
  height = "500px",
}) => {
  const [infoWindowId, setInfoWindowId] = useState<string>("");

  // Get Google Maps API key from environment
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";
  const mapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || "";

  // Filter devices with valid coordinates
  const devicesWithLocation = devices.filter(device => 
    device.geo_lat && device.geo_lng && 
    !isNaN(Number(device.geo_lat)) && !isNaN(Number(device.geo_lng))
  );

  // Calculate center point from devices
  const getMapCenter = () => {
    if (devicesWithLocation.length === 0) {
      return INITIAL_MAP_REGION.center;
    }

    const totalLat = devicesWithLocation.reduce((sum, device) => sum + Number(device.geo_lat), 0);
    const totalLng = devicesWithLocation.reduce((sum, device) => sum + Number(device.geo_lng), 0);

    return {
      lat: totalLat / devicesWithLocation.length,
      lng: totalLng / devicesWithLocation.length,
    };
  };

  // Calculate appropriate zoom level
  const getMapZoom = () => {
    if (devicesWithLocation.length <= 1) return 15;
    
    const latitudes = devicesWithLocation.map(device => Number(device.geo_lat));
    const longitudes = devicesWithLocation.map(device => Number(device.geo_lng));
    
    const latSpan = Math.max(...latitudes) - Math.min(...latitudes);
    const lngSpan = Math.max(...longitudes) - Math.min(...longitudes);
    const maxSpan = Math.max(latSpan, lngSpan);
    
    console.log(maxSpan, latSpan, lngSpan);

    if (maxSpan > 100) return 4;
    if (maxSpan > 50) return 5;
    if (maxSpan > 10) return 6;
    if (maxSpan > 5) return 7;
    if (maxSpan > 1) return 8;
    if (maxSpan > 0.1) return 9;
    if (maxSpan > 0.25) return 12;
    if (maxSpan > 0.01) return 13;
    return 17;
  };

  if (!apiKey) {
    return null;
  }

  return (
    <Box h={height} w="100%" position="relative" borderRadius="md" overflow="hidden">
      {isLoading && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex={1000}
          bg="white"
          p={4}
          borderRadius="md"
          boxShadow="lg"
        >
          <VStack>
            <Spinner size="lg" colorScheme="blue" />
            <Text>Loading devices...</Text>
          </VStack>
        </Box>
      )}

      <APIProvider apiKey={apiKey}>
        <Map
          mapId={mapId}
          defaultCenter={getMapCenter()}
          defaultZoom={getMapZoom()}
          clickableIcons={false}
          gestureHandling="cooperative"
          disableDefaultUI={false}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={true}
          zoomControl={true}
        >
          {devicesWithLocation.map((device) => (
            <MarkerWithInfoWindow
              key={device.id}
              device={device}
              infoWindowId={infoWindowId}
              setInfoWindowId={setInfoWindowId}
              onDeviceView={onDeviceView}
              onDeviceEdit={onDeviceEdit}
            />
          ))}
        </Map>
      </APIProvider>

      {/* Device count overlay */}
      <Box
        position="absolute"
        top={4}
        left={4}
        bg="white"
        px={3}
        py={2}
        borderRadius="md"
        boxShadow="md"
        zIndex={1000}
      >
        <Text fontSize="sm" fontWeight="medium">
          {devicesWithLocation.length} of {devices.length} devices shown
        </Text>
      </Box>
    </Box>
  );
};

export default DeviceMap;

